import * as env from "shared/env";
import * as live from "./live";
import * as mock from "./mock";
import { LeadNote, NewLeadNote } from "model/lead-note";
import { FollowUpBossAgentSelectionRequest } from "model/follow-up-boss-agent-selection";
import { LeadImport } from "model/lead-import";
import { LeadTag, NewLeadTag} from "model/lead-tag";
import { LeadPhone, NewLeadPhone } from "model/lead-phone";
import { NewLeadPipeline } from "model/lead-pipeline";
import { SavedListing, NewSavedListing } from "model/saved-listing";
import { SavedSearch, NewSavedSearch } from "model/saved-search";
import { Campaign, NewCampaign } from "model/campaign";
import { CampaignMessage, NewCampaignMessage } from "model/campaign-message";
import { CampaignCampaignMessage, NewCampaignCampaignMessage } from "model/campaign-campaign-message";
import { CampaignSubscription, NewCampaignSubscription, NewCampaignSubscriptionBatch } from "model/campaign-subscription";
import { LeadAnniversary, NewLeadAnniversary } from "model/lead-anniversary";
import { Task, NewTask } from "model/task";
import { IntegrationPartial } from "model/integration";
import { CampaignRoute, NewCampaignRoute } from "model/campaign-route";
import { CampaignCampaignMessageSendEmail } from "model/campaign-campaign-message-send-email";
import { CampaignsSettings } from "model/campaigns-settings";
import { Agent, NewAgent } from "model/agent";
import { AgentBoard, NewAgentBoard } from "model/agent-board";
import { BoardOption, ClientBoardResource } from "./resource/client-board";
import { NewAgentMlsIdResource } from "./resource/agent-mls-id";
import { LeadProfileSuggestionReferenceResource } from "./resource/lead-profile-suggestion";
import { NewOffice, Office } from "model/office";
import { OfficeBoard, NewOfficeBoard } from "model/office-board";
import { PagePartial } from "model/page";
import { Lead, NewLead } from "model/lead";
import { UserPartial } from "model/user";
import { UserAuth } from "model/user-auth";
import { GeographicRoute, NewGeographicRoute } from "model/geographic-route";
import { NewRoundRobinRoute } from "model/round-robin-route";
import { MarketReportSubscription, NewMarketReportSubscription } from "model/market-report-subscription";
import { Market, NewMarket } from "model/market";
import { MarketReport } from "model/market-report";
import { MarketSettings } from "model/market-settings";
import { NylasTokenRequest } from "./resource/nylas";
import { NaturalLanguageSearchSuggestion, NewNaturalLanguageSearchSuggestion } from "model/natural-language-search-suggestion";
import { AreaSelectorPlaceholder, NewAreaSelectorPlaceholder } from "model/area-selector-placeholder";
import { NewPing } from "model/ping";
import { UpsertEmailRoutingRuleResource } from "./resource/email-routing-rule";
import { CreateEmailRecipientResource } from "./resource/email-recipient";
import { UpsertOffMarketListingResource } from "./resource/off-market-listing";
import { PriceRangeOptionsResource } from "./resource/price-range-options";
import { UpdateActiveListingModifyResource } from "./resource/active-listing-modify";
import { UpsertClientPropertyResource } from "./resource/client-property";
import {
	ListingPhotoKey,
	AddListingPhotoResource,
	ReorderListingPhotosResource,
	MakeMainListingPhotoResource,
} from "./resource/listing-photo";
import { LeadContactReportQuery } from "./resource/lead-contact-report";
import { SavedSearchActivityQuery } from "./resource/saved-search-activity";
import { MobileActivityQuery } from "./resource/mobile-activity";
import { EmailBrandingResource } from "./resource/email-branding";
import { MobileSettingsResource } from "./resource/mobile-settings";
import { SearchCitiesResource } from "./resource/search-cities";
import { LocationsResource } from "./resource/locations";
import { PropertyTypesResource } from "./resource/property-types";

export const createPing = (ping: NewPing) => {
    return api().createPing(ping);
}

export const getUser = () => {
    return api().getUser();
}

export const profileSync = () => {
    return api().profileSync();
}

export const saveUser = (user: UserPartial) => {
    return api().saveUser(user);
}

export const saveUserAuth = (userAuth: UserAuth) => {
    return api().saveUserAuth(userAuth);
}

export const getPriceRangeOptions = () => {
    return api().getPriceRangeOptions();
}

export const savePriceRangeOptions = (options: PriceRangeOptionsResource) => {
    return api().savePriceRangeOptions(options);
}

export const getAdminMode = () => {
    return api().getAdminMode();
}

export const getResellerById = (id: number) => {
    return api().getResellerById(id);
}

export const getClient = (clientId: number) => {
    return api().getClient(clientId);
}


export const getPostalCodes = () => {
    return api().getPostalCodes();
}


export const getQuota = () => {
    return api().getQuota();
}

export const getLeads = () => {
    return api().getLeads();
}

export const createLead = (lead: NewLead) => {
	return api().createLead(lead);
}

export const updateLead = (lead: Lead) => {
	return api().updateLead(lead);
}

export const saveLeadImport = (leadImports: LeadImport[]) => {
    return api().saveLeadImport(leadImports);
}

export const getSavedListings = (leadId: number) => {
    return api().getSavedListings(leadId);
}

export const createSavedListing = (savedListing: NewSavedListing) => {
	return api().createSavedListing(savedListing);
}

export const updateSavedListing = (savedListing: SavedListing) => {
	return api().updateSavedListing(savedListing);
}

export const getSavedSearches = () => {
    return api().getSavedSearches();
}

export const createSavedSearch = (savedSearch: NewSavedSearch) => {
	return api().createSavedSearch(savedSearch);
}

export const updateSavedSearch = (savedSearch: SavedSearch) => {
	return api().updateSavedSearch(savedSearch);
}

export const getSavedSearchResultTotal = (savedSearchId: number) => {
    return api().getSavedSearchResultTotal(savedSearchId);
}

export const getLeadSearchProfile = (leadId: number) => {
    return api().getLeadSearchProfile(leadId);
}

export const deleteLeadSearchProfileHistory = (leadId: number) => {
    return api().deleteLeadSearchProfileHistory(leadId);
}

export const getLeadConversationHistory = (leadId: number) => {
    return api().getLeadConversationHistory(leadId);
}

export const getLeadConversationHistoryDay = (leadId: number, date: string) => {
    return api().getLeadConversationHistoryDay(leadId, date);
}

export const getLeadProfileSuggestions = (leadId: number) => {
    return api().getLeadProfileSuggestions(leadId);
}

export const acceptLeadProfileSuggestions = (leadId: number, suggestions: LeadProfileSuggestionReferenceResource[]) => {
    return api().acceptLeadProfileSuggestions(leadId, suggestions);
}

export const dismissLeadProfileSuggestions = (leadId: number, suggestions: LeadProfileSuggestionReferenceResource[]) => {
    return api().dismissLeadProfileSuggestions(leadId, suggestions);
}

export const getEmailSents = (leadId: number) => {
    return api().getEmailSents(leadId);
}

export const getEmailClicks = (leadId: number) => {
    return api().getEmailClicks(leadId);
}

export const getLeadNotes = () => {
    return api().getLeadNotes();
}

export const createLeadNote = (leadNote: NewLeadNote) => {
	return api().createLeadNote(leadNote);
}

export const updateLeadNote = (leadNote: LeadNote) => {
	return api().updateLeadNote(leadNote);
}

export const getLeadTags = () => {
    return api().getLeadTags();
}
export const createLeadTag = (leadTag: NewLeadTag) => {
	return api().createLeadTag(leadTag);
}

export const deleteLeadTag = (leadTag: LeadTag) => {
    return api().deleteLeadTag(leadTag);
}

export const getLeadPipelines = () => {
    return api().getLeadPipelines();
}

export const createLeadPipeline = (leadPipeline: NewLeadPipeline) => {
    return api().createLeadPipeline(leadPipeline);
}

export const getLeadPhones = () => {
    return api().getLeadPhones();
}

export const createLeadPhone = (leadPhone: NewLeadPhone) => {
	return api().createLeadPhone(leadPhone);
}

export const updateLeadPhone = (leadPhone: LeadPhone) => {
	return api().updateLeadPhone(leadPhone);
}

export const getLeadAnniversaries = () => {
    return api().getLeadAnniversaries();
}

export const updateLeadAnniversary = (leadAnniversary: LeadAnniversary) => {
	return api().updateLeadAnniversary(leadAnniversary);
}

export const createLeadAnniversary = (leadAnniversary: NewLeadAnniversary) => {
	return api().createLeadAnniversary(leadAnniversary);
}

export const getLeadActivities = (leadId?: number) => {
    return api().getLeadActivities(leadId);
}

export const getLeadActivityStats = () => {
    return api().getLeadActivityStats();
}

export const getLeadLastActivities = () => {
    return api().getLeadLastActivities();
}

export const getLeadLastSearchAgentChats = () => {
    return api().getLeadLastSearchAgentChats();
}

export const getCampaigns = () => {
    return api().getCampaigns();
}

export const getCampaignDisplayCategories = () => {
    return api().getCampaignDisplayCategories();
}

export const createCampaignDisplayCategory = (name: string) => {
    return api().createCampaignDisplayCategory(name);
}

export const updateCampaignDisplayCategory = (id: number, name: string) => {
    return api().updateCampaignDisplayCategory(id, name);
}

export const deleteCampaignDisplayCategory = (id: number, moveToId?: number | null) => {
    return api().deleteCampaignDisplayCategory(id, moveToId);
}

export const updateCampaign = (campaign: Campaign) => {
    return api().updateCampaign(campaign);
}

export const createCampaign = (campaign: NewCampaign) => {
    return api().createCampaign(campaign);
}

export const getGeographicRoutes = () => {	
	return api().getGeographicRoutes();
}

export const createGeographicRoute = (geographicRoute: NewGeographicRoute) => {
	return api().createGeographicRoute(geographicRoute);
}

export const deleteGeographicRoute = (geographicRoute: GeographicRoute) => {
	return api().deleteGeographicRoute(geographicRoute);
}

export const getRoundRobinRoutes = () => {
	return api().getRoundRobinRoutes();
}

export const updateRoundRobinRoutes = (roundRobinRoutes: NewRoundRobinRoute[]) => {
	return api().updateRoundRobinRoutes(roundRobinRoutes);
}

export const getCampaignMessages = () => {
    return api().getCampaignMessages();
}

export const resendTextingOtpVerification = () => {
	return api().resendTextingOtpVerification();
}

export const getTextingTaxId = () => {
	return api().getTextingTaxId();
}

export const createCampaignMessage = (campaignMessage: NewCampaignMessage) => {
    return api().createCampaignMessage(campaignMessage);
}

export const updateCampaignMessage = (campaignMessage: CampaignMessage) => {
    return api().updateCampaignMessage(campaignMessage);
}

export const deleteCampaignMessage = (campaignMessage: CampaignMessage) => {
    return api().deleteCampaignMessage(campaignMessage);
}

export const getCampaignCampaignMessages = () => {
    return api().getCampaignCampaignMessages();
}

export const createCampaignCampaignMessage = (campaignCampaignMessage: NewCampaignCampaignMessage) => {
    return api().createCampaignCampaignMessage(campaignCampaignMessage);
}

export const updateCampaignCampaignMessage = (campaignCampaignMessage: CampaignCampaignMessage) => {
    return api().updateCampaignCampaignMessage(campaignCampaignMessage);
}

export const updateCampaignCampaignMessages = (campaignCampaignMessages: CampaignCampaignMessage[]) => {
    return api().updateCampaignCampaignMessages(campaignCampaignMessages);
}

export const sendCampaignCampagaignMessageEmailTest = (campaignCampaignMessageSendEmail: CampaignCampaignMessageSendEmail) => {
    return api().sendCampaignCampagaignMessageEmailTest(campaignCampaignMessageSendEmail);
}

export const getCampaignSubscriptions = () => {
    return api().getCampaignSubscriptions();
}

export const createCampaignSubscription = (campaignSubscription: NewCampaignSubscription) => {
	return api().createCampaignSubscription(campaignSubscription);
};

export const createCampaignSubscriptionsBatch = (batch: NewCampaignSubscriptionBatch) => {
	return api().createCampaignSubscriptionsBatch(batch);
};

export const updateCampaignSubscription = (campaignSubscription: CampaignSubscription) => {
	return api().updateCampaignSubscription(campaignSubscription);
};

export const getCampaignEmails = (leadId?: number, campaignId?: number, blast?: boolean) => {
    return api().getCampaignEmails(leadId, campaignId, blast);
}

export const getCampaignStats = () => {
    return api().getCampaignStats();
}

export const getCampaignSubscriptionStats = () => {
    return api().getCampaignSubscriptionStats();
}

export const getCampaignCampaignMessageStats = () => {
    return api().getCampaignCampaignMessageStats();
}

export const getCampaignRoutes = () => {
    return api().getCampaignRoutes();
}

export const createCampaignRoute = (campaignRoute: NewCampaignRoute) => {
	return api().createCampaignRoute(campaignRoute);
};

export const updateCampaignRoute = (campaignRoute: CampaignRoute) => {
	return api().updateCampaignRoute(campaignRoute);
};

export const deleteCampaignRoute = (campaignRoute: CampaignRoute) => {
    return api().deleteCampaignRoute(campaignRoute);
}

export const getCampaignsSettings = () => {
    return api().getCampaignsSettings();
}

export const updateCampaignsSettings = (campaignsSettings: CampaignsSettings) => {
    return api().updateCampaignsSettings(campaignsSettings);
}

export const getMarkets = () => {
    return api().getMarkets();
}

export const createMarket = (market: NewMarket) => {
    return api().createMarket(market);
}

export const updateMarket = (market: Market) => {
    return api().updateMarket(market);
}

export const getMarketSettings = () => {
    return api().getMarketSettings();
}

export const updateMarketSettings = (marketSettings: MarketSettings) => {
    return api().updateMarketSettings(marketSettings);
}

export const getMobileSettings = () => {
    return api().getMobileSettings();
}

export const updateMobileSettings = (body: MobileSettingsResource) => {
    return api().updateMobileSettings(body);
}

export const getSearchCities = () => {
    return api().getSearchCities();
}

export const updateSearchCities = (body: SearchCitiesResource) => {
    return api().updateSearchCities(body);
}

export const getLocations = () => {
    return api().getLocations();
}

export const updateLocations = (body: LocationsResource) => {
    return api().updateLocations(body);
}

export const getPropertyTypes = () => {
    return api().getPropertyTypes();
}

export const updatePropertyTypes = (body: PropertyTypesResource) => {
    return api().updatePropertyTypes(body);
}

export const restorePropertyTypes = () => {
    return api().restorePropertyTypes();
}

export const getMarketListings = () => {
    return api().getMarketListings();
}

export const getMarketReports = () => {
    return api().getMarketReports();
}

export const updateMarketReport = (marketReport: MarketReport, marketId: number) => {
    return api().updateMarketReport(marketReport, marketId);
}

export const getMarketReportSubscriptions = () => {
    return api().getMarketReportSubscriptions();
}

export const createMarketReportSubscription = (subscription: NewMarketReportSubscription) => {
    return api().createMarketReportSubscription(subscription);
}

export const updateMarketReportSubscription = (subscription: MarketReportSubscription) => {
    return api().updateMarketReportSubscription(subscription);
}

export const updateMarketSubscriptions = (marketId: number, subscriptions: (NewMarketReportSubscription | MarketReportSubscription)[]) => {
    return api().updateMarketSubscriptions(marketId, subscriptions);
}

export const getAgents = () => {
    return api().getAgents();
}

export const createAgent = (agent: NewAgent) => {
	return api().createAgent(agent);
}

export const updateAgent = (agent: Agent) => {
	return api().updateAgent(agent);
};

export const getAgentTestimonials = (agentId: number) => {
	return api().getAgentTestimonials(agentId);
};

export const createAgentTestimonial = (agentId: number, testimonial: { quote: string; author: string }) => {
	return api().createAgentTestimonial(agentId, testimonial);
};

export const updateAgentTestimonial = (agentId: number, testimonialId: number, testimonial: { quote: string; author: string }) => {
	return api().updateAgentTestimonial(agentId, testimonialId, testimonial);
};

export const deleteAgentTestimonial = (agentId: number, testimonialId: number) => {
	return api().deleteAgentTestimonial(agentId, testimonialId);
};

export const updateAgents = (agents: Agent[]) => {
	return api().updateAgents(agents);
};

export const deleteAgent = (agent: Agent) => {
    return api().deleteAgent(agent);
}

export const getOffices = () => {
    return api().getOffices();
}

export const updateOffices = (offices: Office[]) => {
	return api().updateOffices(offices);
};

export const updateOffice = (office: Office) => {
	return api().updateOffice(office);
}

export const createOffice = (office: NewOffice) => {
    return api().createOffice(office);
}

export const deleteOffice = (office: Office) => {
    return api().deleteOffice(office);
}

export const getCities = () => {
    return api().getCities();
}

export const getNeighborhoods = () => {
    return api().getNeighborhoods();
}

export const getAggregateStats = () => {
    return api().getAggregateStats();
}

export const getTasks = () => {
    return api().getTasks();
}
export const createTask = (task: NewTask) => {
	return api().createTask(task);
};

export const updateTask = (task: Task) => {
	return api().updateTask(task);
};

export const getIntegrations = () => {
    return api().getIntegrations();
}

export const saveIntegration = (integration: IntegrationPartial) => {
    return api().saveIntegration(integration);
}

export const deleteIntegration = (integration: IntegrationPartial) => {
    return api().deleteIntegration(integration);
}

export const testIntegration = (integration: IntegrationPartial) => {
    return api().testIntegration(integration);
}

export const getIntegrationCalendars = (integrationId: number) => {
    return api().getIntegrationCalendars(integrationId);
}

export const getFacebookPages = () => {
    return api().getFacebookPages();
}

export const connectFollowUpBoss = (integrationId: number, apiKey: string) => {
    return api().connectFollowUpBoss(integrationId, apiKey);
}

export const disconnectFollowUpBoss = (integrationId: number) => {
    return api().disconnectFollowUpBoss(integrationId);
}

export const getFollowUpBossStatus = (integrationId: number) => {
    return api().getFollowUpBossStatus(integrationId);
}

export const startFollowUpBossBackfill = (integrationId: number) => {
    return api().startFollowUpBossBackfill(integrationId);
}

export const getFollowUpBossAgentSelection = (integrationId: number) => {
    return api().getFollowUpBossAgentSelection(integrationId);
}

export const saveFollowUpBossAgentSelection = (integrationId: number, body: FollowUpBossAgentSelectionRequest) => {
    return api().saveFollowUpBossAgentSelection(integrationId, body);
}


export const getSiteLinks = () => {
    return api().getSiteLinks();
}

export const getBoards = () => {
    return api().getBoards();
}

export const getAgentBoards = () => {
    return api().getAgentBoards();
}

export const createAgentBoard = (agentBoard: NewAgentBoard) => {
    return api().createAgentBoard(agentBoard);
}

export const updateAgentBoard = (agentBoard: AgentBoard) => {
    return api().updateAgentBoard(agentBoard);
}

export const deleteAgentBoard = (agentBoard: AgentBoard) => {
    return api().deleteAgentBoard(agentBoard);
}

export const getAgentMlsIds = () => {
    return api().getAgentMlsIds();
}

export const createAgentMlsId = (agentMlsId: NewAgentMlsIdResource) => {
    return api().createAgentMlsId(agentMlsId);
}

export const updateAgentMlsId = (id: number, agentMlsId: NewAgentMlsIdResource) => {
    return api().updateAgentMlsId(id, agentMlsId);
}

export const deleteAgentMlsId = (id: number) => {
    return api().deleteAgentMlsId(id);
}

export const getClientBoards = () => {
    return api().getClientBoards();
}

export const updateClientBoard = (id: number, clientBoard: ClientBoardResource, strict = false) => {
    return api().updateClientBoard(id, clientBoard, strict);
}

export const addClientBoard = (clientBoard: ClientBoardResource, strict = false) => {
    return api().addClientBoard(clientBoard, strict);
}

export const getClientBoardOptions = (): Promise<BoardOption[]> => {
    return api().getClientBoardOptions();
}

export const getOfficeBoards = () => {
    return api().getOfficeBoards();
}

export const createOfficeBoard = (officeBoard: NewOfficeBoard) => {
	return api().createOfficeBoard(officeBoard);
}

export const updateOfficeBoard = (officeBoard: OfficeBoard) => {
	return api().updateOfficeBoard(officeBoard);
}

export const deleteOfficeBoard = (officeBoard: OfficeBoard) => {
    return api().deleteOfficeBoard(officeBoard);
}

export const getUploadSignature = (uploadPreset: string) => {
    return api().getUploadSignature(uploadPreset);
}

export const getGeocode = (address?: string) => {
    return api().getGeocode(address);
}

export const getPages = () => {
    return api().getPages();
}

export const savePage = (page: PagePartial) => {
    return api().savePage(page);
}

export const getLanguages = () => {
    return api().getLanguages();
}

export const exchangeNylasToken = async (obj: NylasTokenRequest) => {
	return await api().exchangeNylasToken(obj);
}

export const updateSplashScreen = ({ id, splashScreenSkipDate }: { id: number; splashScreenSkipDate: string }) => {
		return api().updateSplashScreenSettings({ id, splashScreenSkipDate })
}

export const getNaturalLanguageSearchSuggestions = () => {
	return api().getNaturalLanguageSearchSuggestions();
}

export const createNaturalLanguageSearchSuggestion = (suggestion: NewNaturalLanguageSearchSuggestion) => {
	return api().createNaturalLanguageSearchSuggestion(suggestion);
}

export const updateNaturalLanguageSearchSuggestion = (suggestion: NaturalLanguageSearchSuggestion) => {
	return api().updateNaturalLanguageSearchSuggestion(suggestion);
}

export const deleteNaturalLanguageSearchSuggestion = (id: number) => {
	return api().deleteNaturalLanguageSearchSuggestion(id);
}

export const getAreaSelectorPlaceholders = () => {
	return api().getAreaSelectorPlaceholders();
}

export const createAreaSelectorPlaceholder = (placeholder: NewAreaSelectorPlaceholder) => {
	return api().createAreaSelectorPlaceholder(placeholder);
}

export const updateAreaSelectorPlaceholder = (placeholder: AreaSelectorPlaceholder) => {
	return api().updateAreaSelectorPlaceholder(placeholder);
}

export const deleteAreaSelectorPlaceholder = (id: number) => {
	return api().deleteAreaSelectorPlaceholder(id);
}

export const getEmailRoutingRules = () => {
	return api().getEmailRoutingRules();
}

export const bulkUpsertEmailRoutingRules = (body: UpsertEmailRoutingRuleResource[]) => {
	return api().bulkUpsertEmailRoutingRules(body);
}

export const getEmailRecipients = () => {
	return api().getEmailRecipients();
}

export const addEmailRecipients = (body: CreateEmailRecipientResource[]) => {
	return api().addEmailRecipients(body);
}

export const deleteEmailRecipient = (clientEmailRecipientId: number) => {
	return api().deleteEmailRecipient(clientEmailRecipientId);
}

export const getOffMarketListings = () => {
	return api().getOffMarketListings();
}

export const createOffMarketListing = (body: UpsertOffMarketListingResource) => {
	return api().createOffMarketListing(body);
}

export const updateOffMarketListing = (id: number, body: UpsertOffMarketListingResource) => {
	return api().updateOffMarketListing(id, body);
}

export const deleteOffMarketListing = (id: number) => {
	return api().deleteOffMarketListing(id);
}

export const getClientProperties = () => {
	return api().getClientProperties();
}

export const getClientProperty = (id: number) => {
	return api().getClientProperty(id);
}

export const createClientProperty = (body: UpsertClientPropertyResource) => {
	return api().createClientProperty(body);
}

export const updateClientProperty = (id: number, body: UpsertClientPropertyResource) => {
	return api().updateClientProperty(id, body);
}

export const deleteClientProperty = (id: number) => {
	return api().deleteClientProperty(id);
}

export const getListingPhotos = (key: ListingPhotoKey) => {
	return api().getListingPhotos(key);
}

export const addListingPhoto = (key: ListingPhotoKey, body: AddListingPhotoResource) => {
	return api().addListingPhoto(key, body);
}

export const reorderListingPhotos = (key: ListingPhotoKey, body: ReorderListingPhotosResource) => {
	return api().reorderListingPhotos(key, body);
}

export const deleteListingPhoto = (propertyPhotoId: number, key: ListingPhotoKey) => {
	return api().deleteListingPhoto(propertyPhotoId, key);
}

export const makeMainListingPhoto = (key: ListingPhotoKey, body: MakeMainListingPhotoResource) => {
	return api().makeMainListingPhoto(key, body);
}

export const revertListingPhotosToMls = (key: ListingPhotoKey) => {
	return api().revertListingPhotosToMls(key);
}

export const getLeadContactReport = (params: LeadContactReportQuery) => {
	return api().getLeadContactReport(params);
}

export const getContactEventTypes = () => {
	return api().getContactEventTypes();
}

export const getSavedSearchActivity = (params: SavedSearchActivityQuery) => {
	return api().getSavedSearchActivity(params);
}

export const getSavedListingsReport = (startDate?: string, endDate?: string) => {
	return api().getSavedListingsReport(startDate, endDate);
}

export const getMobileActivity = (params: MobileActivityQuery) => {
	return api().getMobileActivity(params);
}

export const exportLeads = (orderBy: string, includeDisabled: boolean, includeDeleted: boolean) => {
	return api().exportLeads(orderBy, includeDisabled, includeDeleted);
}

export const exportAgents = () => {
	return api().exportAgents();
}

export const getActiveListings = (params: { agentId?: number; featuredOnlyYn?: boolean; activeFeaturedForReport?: boolean; context?: string }) => {
	return api().getActiveListings(params);
}

export const getActiveListingModify = (params: { listingNumber: string; boardId: number }) => {
	return api().getActiveListingModify(params);
}

export const updateActiveListingModify = (
	params: { listingNumber: string; boardId: number },
	body: UpdateActiveListingModifyResource
) => {
	return api().updateActiveListingModify(params, body);
}

export const getEmailBranding = () => {
	return api().getEmailBranding();
}

export const updateEmailBranding = (body: EmailBrandingResource) => {
	return api().updateEmailBranding(body);
}

export const previewEmailBranding = (body: EmailBrandingResource) => {
	return api().previewEmailBranding(body);
}

export const getBilling = () => {
	return api().getBilling();
}

export const updateBillingCard = (token: string) => {
	return api().updateBillingCard(token);
}

export const getBillingInvoices = () => {
	return api().getBillingInvoices();
}

export const downloadBillingInvoice = (displayNumber: string) => {
	return api().downloadBillingInvoice(displayNumber);
}

const api = (): typeof live | typeof mock => {
    if (env.live) {
        return live;
    } else {
        return mock;
    }
}

